import React, { useState, useEffect } from 'react';
import { User, Loader2, Phone, Mail } from 'lucide-react';
import { expertDashboardApi, expertAuthApi, getErrorMessage } from '../../../lib/http';
import { ExpertProfile } from '../../../types';
import toast from 'react-hot-toast';

type PhoneStep = 'display' | 'edit' | 'verify' | 'verified';
type EmailStep = 'display' | 'edit' | 'verify';

interface ProfileSettingsProps {
  profile: ExpertProfile;
  onUpdate: (profile: ExpertProfile) => void;
}

export const ProfileSettings: React.FC<ProfileSettingsProps> = ({
  profile,
  onUpdate,
}) => {
  const [displayName, setDisplayName] = useState(profile.display_name || '');
  const [businessName, setBusinessName] = useState(profile.business_name || '');
  const [description, setDescription] = useState(profile.description || '');
  const aboutMaxLength = 1000;
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Phone verification state
  const [phoneStep, setPhoneStep] = useState<PhoneStep>('display');
  const [newPhone, setNewPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isPhoneLoading, setIsPhoneLoading] = useState(false);

  // Email change state
  const [emailStep, setEmailStep] = useState<EmailStep>('display');
  const [newEmail, setNewEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [emailResendCooldown, setEmailResendCooldown] = useState(0);

  // Resend cooldown timer (phone)
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Resend cooldown timer (email)
  useEffect(() => {
    if (emailResendCooldown > 0) {
      const timer = setTimeout(() => setEmailResendCooldown(emailResendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [emailResendCooldown]);

  const hasChanges =
    displayName !== (profile.display_name || '') ||
    businessName !== (profile.business_name || '') ||
    description !== (profile.description || '') ||
    (verificationToken && newPhone);

  const handleSave = async () => {
    if (!displayName.trim()) {
      setError('Display name is required');
      return;
    }

    if (description.length > aboutMaxLength) {
      setError(`About text is limited to ${aboutMaxLength} characters.`);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const updateData: Record<string, string | undefined> = {
        display_name: displayName,
        business_name: businessName,
        description: description,
      };

      // Include phone change if verified
      if (verificationToken && newPhone) {
        updateData.phone = newPhone;
        updateData.phone_verification_token = verificationToken;
      }

      const response = await expertDashboardApi.profile.update(updateData);
      onUpdate(response.data);
      setVerificationToken(null);
      setNewPhone('');
      setPhoneStep('display');
      toast.success('Profile updated!');
    } catch (err) {
      const msg = getErrorMessage(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  // Phone verification handlers
  const handleSendCode = async () => {
    setError(null);
    setIsPhoneLoading(true);
    try {
      const normalizedPhone = newPhone.replace(/[\s\-\(\)]/g, '');
      await expertAuthApi.sendPhoneCode(normalizedPhone);
      setNewPhone(normalizedPhone);
      setPhoneStep('verify');
      setResendCooldown(60);
      setVerificationCode('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError(null);
    setIsPhoneLoading(true);
    try {
      const response = await expertAuthApi.verifyPhoneCode(newPhone, verificationCode);
      if (response.data.verification_token) {
        setVerificationToken(response.data.verification_token);
        setPhoneStep('verified');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsPhoneLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    setError(null);
    setIsPhoneLoading(true);
    try {
      await expertAuthApi.sendPhoneCode(newPhone);
      setResendCooldown(60);
      setVerificationCode('');
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsPhoneLoading(false);
    }
  };

  // Email change handlers
  const handleSendEmailCode = async () => {
    setEmailError(null);
    setIsEmailLoading(true);
    try {
      await expertDashboardApi.emailChange.sendCode(newEmail);
      setEmailStep('verify');
      setEmailResendCooldown(60);
      setEmailOtp('');
    } catch (err) {
      setEmailError(getErrorMessage(err));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleVerifyEmailCode = async () => {
    setEmailError(null);
    setIsEmailLoading(true);
    try {
      await expertDashboardApi.emailChange.verify(emailOtp, newEmail);
      const profileResponse = await expertDashboardApi.profile.get();
      onUpdate(profileResponse.data);
      setEmailStep('display');
      setNewEmail('');
      setEmailOtp('');
      toast.success('Check your new email for a confirmation link');
    } catch (err) {
      setEmailError(getErrorMessage(err));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleCancelEmailChange = async () => {
    setIsEmailLoading(true);
    try {
      await expertDashboardApi.emailChange.cancel();
      const profileResponse = await expertDashboardApi.profile.get();
      onUpdate(profileResponse.data);
    } catch (err) {
      setEmailError(getErrorMessage(err));
    } finally {
      setIsEmailLoading(false);
    }
  };

  const handleResendEmailCode = async () => {
    if (emailResendCooldown > 0) return;
    setEmailError(null);
    setIsEmailLoading(true);
    try {
      await expertDashboardApi.emailChange.sendCode(newEmail);
      setEmailResendCooldown(60);
      setEmailOtp('');
    } catch (err) {
      setEmailError(getErrorMessage(err));
    } finally {
      setIsEmailLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-pink-100 rounded-lg">
          <User className="w-5 h-5 text-pink-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Profile</h3>
          <p className="text-sm text-gray-500">
            Your display name and contact phone visible on your booking page
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      )}

      <div className="space-y-5">
        {/* Display Name */}
        <div>
          <label htmlFor="display_name" className="block text-sm font-medium text-gray-700 mb-1">
            Display Name
          </label>
          <input
            id="display_name"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="Your name"
            maxLength={100}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Visible to clients on your public booking page
          </p>
        </div>

        {/* Business Name */}
        <div>
          <label htmlFor="business_name" className="block text-sm font-medium text-gray-700 mb-1">
            Business Name
          </label>
          <input
            id="business_name"
            type="text"
            value={businessName}
            onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Business or studio name (optional)"
            maxLength={100}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
          />
          <p className="mt-1 text-xs text-gray-500">
            Shown as the title on your public booking page. Leave blank to use your display name.
          </p>
        </div>

        {/* About / Description */}
        <div>
          <label htmlFor="about" className="block text-sm font-medium text-gray-700 mb-1">
            About
          </label>
          <textarea
            id="about"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Tell clients about you, your services, and what makes you great."
            rows={5}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 resize-y"
          />
          <div className="mt-1 flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Free-form bio shown on your public booking page.
            </p>
            <p
              className={`text-xs tabular-nums ${
                description.length > aboutMaxLength ? 'text-red-600' : 'text-gray-500'
              }`}
            >
              {description.length}/{aboutMaxLength}
            </p>
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <span className="flex items-center gap-1.5">
              <Mail className="w-3.5 h-3.5" />
              Email
            </span>
          </label>

          {emailError && (
            <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between">
              <span>{emailError}</span>
              <button type="button" onClick={() => setEmailError(null)} className="text-red-700 hover:text-red-900">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          )}

          {/* Display current email */}
          {emailStep === 'display' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <input
                  type="email"
                  value={profile.email || ''}
                  disabled
                  className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 cursor-not-allowed"
                />
                <button
                  type="button"
                  onClick={() => { setEmailStep('edit'); setNewEmail(''); setEmailError(null); }}
                  className="px-4 py-2 text-sm font-medium text-pink-600 border border-pink-300 rounded-lg hover:bg-pink-50"
                >
                  Change
                </button>
              </div>
              {profile.unconfirmed_email && (
                <div className="flex items-center justify-between px-3 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <span className="text-sm text-yellow-700">
                    Pending change to <span className="font-medium">{profile.unconfirmed_email}</span> — check your inbox
                  </span>
                  <button
                    type="button"
                    onClick={handleCancelEmailChange}
                    disabled={isEmailLoading}
                    className="text-sm font-medium text-yellow-700 hover:text-yellow-900 underline disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Edit: enter new email */}
          {emailStep === 'edit' && (
            <div className="space-y-2">
              <input
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="Enter new email address"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
              <p className="text-xs text-gray-500">We'll send a verification code to your current email to confirm your identity</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSendEmailCode}
                  disabled={isEmailLoading || !newEmail.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-pink-600 rounded-lg hover:bg-pink-700 disabled:opacity-50"
                >
                  {isEmailLoading ? 'Sending...' : 'Send verification code'}
                </button>
                <button
                  type="button"
                  onClick={() => { setEmailStep('display'); setNewEmail(''); setEmailError(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Verify: enter OTP */}
          {emailStep === 'verify' && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                Enter the code sent to <span className="font-medium text-pink-600">{profile.email}</span>
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={emailOtp}
                onChange={(e) => setEmailOtp(e.target.value)}
                placeholder="123456"
                maxLength={6}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-center text-xl tracking-widest"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleVerifyEmailCode}
                  disabled={isEmailLoading || !emailOtp.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-pink-600 rounded-lg hover:bg-pink-700 disabled:opacity-50"
                >
                  {isEmailLoading ? 'Verifying...' : 'Verify & change'}
                </button>
                <button
                  type="button"
                  onClick={handleResendEmailCode}
                  disabled={emailResendCooldown > 0 || isEmailLoading}
                  className="px-4 py-2 text-sm font-medium text-pink-600 border border-pink-300 rounded-lg hover:bg-pink-50 disabled:text-gray-400 disabled:border-gray-200"
                >
                  {emailResendCooldown > 0 ? `Resend in ${emailResendCooldown}s` : 'Resend'}
                </button>
                <button
                  type="button"
                  onClick={() => { setEmailStep('edit'); setEmailOtp(''); setEmailError(null); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Back
                </button>
              </div>
            </div>
          )}

          <p className="mt-1 text-xs text-gray-500">
            Your login email. Requires verification to change.
          </p>
        </div>

        {/* Contact Phone */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <span className="flex items-center gap-1.5">
              <Phone className="w-3.5 h-3.5" />
              Contact Phone
            </span>
          </label>

          {/* Display current phone */}
          {phoneStep === 'display' && (
            <div className="flex items-center gap-2">
              <input
                type="tel"
                value={profile.phone || ''}
                disabled
                placeholder="No phone number set"
                className="flex-1 px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 cursor-not-allowed"
              />
              <button
                type="button"
                onClick={() => {
                  setPhoneStep('edit');
                  setNewPhone(profile.phone || '');
                }}
                className="px-4 py-2 text-sm font-medium text-pink-600 border border-pink-300 rounded-lg hover:bg-pink-50"
              >
                Change
              </button>
            </div>
          )}

          {/* Edit: enter new phone */}
          {phoneStep === 'edit' && (
            <div className="space-y-2">
              <input
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="+1234567890"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
              />
              <p className="text-xs text-gray-500">We'll send a verification code to this number</p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleSendCode}
                  disabled={isPhoneLoading || !newPhone.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-pink-600 rounded-lg hover:bg-pink-700 disabled:opacity-50"
                >
                  {isPhoneLoading ? 'Sending...' : 'Send Code'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPhoneStep('display');
                    setNewPhone('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Verify: enter code */}
          {phoneStep === 'verify' && (
            <div className="space-y-2">
              <p className="text-sm text-gray-600">
                Enter the code sent to <span className="font-medium text-pink-600">{newPhone}</span>
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="123456"
                maxLength={8}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500 text-center text-xl tracking-widest"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleVerifyCode}
                  disabled={isPhoneLoading || !verificationCode.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-pink-600 rounded-lg hover:bg-pink-700 disabled:opacity-50"
                >
                  {isPhoneLoading ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={resendCooldown > 0 || isPhoneLoading}
                  className="px-4 py-2 text-sm font-medium text-pink-600 border border-pink-300 rounded-lg hover:bg-pink-50 disabled:text-gray-400 disabled:border-gray-200"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPhoneStep('edit');
                    setVerificationCode('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Change number
                </button>
              </div>
            </div>
          )}

          {/* Verified: show success */}
          {phoneStep === 'verified' && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm text-green-700">
                  <span className="font-medium">{newPhone}</span> verified. Click Save to apply.
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setPhoneStep('display');
                  setNewPhone('');
                  setVerificationToken(null);
                  setVerificationCode('');
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel phone change
              </button>
            </div>
          )}

          <p className="mt-1 text-xs text-gray-500">
            Contact phone for your clients. Requires SMS verification.
          </p>
        </div>
      </div>

      {/* Save Button */}
      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={!hasChanges || isSaving}
          className="px-4 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
        >
          {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default ProfileSettings;
